import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { Search, Crown, Loader2, List, Layers } from 'lucide-react'
import { toast } from 'sonner'
import {
  useKeywordSuggestions,
  useSearchIntent,
  useImportKeywords,
  type KeywordSuggestion,
} from '../hooks/useLabs'
import { useClusterKeywords, type KeywordCluster } from '../hooks/useClusters'

function formatNumber(n: number): string {
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K'
  return String(n)
}

const INTENT_STYLES: Record<string, { label: string; className: string }> = {
  informational: { label: 'Info', className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  navigational: { label: 'Nav', className: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
  commercial: { label: 'Comm', className: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' },
  transactional: { label: 'Trans', className: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
}

function IntentBadge({ intent }: { intent: string }) {
  const style = INTENT_STYLES[intent] ?? { label: intent, className: 'bg-muted text-muted-foreground' }
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${style.className}`}>
      {style.label}
    </span>
  )
}

function TrendSparkline({ data }: { data: Array<{ search_volume: number }> | null }) {
  if (!data?.length) return <span className="text-xs text-muted-foreground">-</span>
  const recent = data.slice(-6)
  const max = Math.max(...recent.map((d) => d.search_volume), 1)
  return (
    <div className="flex items-end gap-px h-4" title="6-month trend">
      {recent.map((d, i) => (
        <div
          key={i}
          className="w-1.5 bg-primary/60 rounded-sm"
          style={{ height: `${Math.max((d.search_volume / max) * 100, 8)}%` }}
        />
      ))}
    </div>
  )
}

function ManagedTierGate() {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <Crown className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
        <h3 className="text-lg font-semibold mb-1">Managed Tier Required</h3>
        <p className="text-sm text-muted-foreground max-w-md mx-auto">
          Keyword research features are available for managed sites. Enable managed tier in the Schedule tab.
        </p>
      </CardContent>
    </Card>
  )
}

function ClusteredView({ clusters }: { clusters: KeywordCluster[] }) {
  return (
    <div className="space-y-4">
      {clusters.map((cluster) => (
        <Card key={cluster.topic}>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <CardTitle className="text-sm">{cluster.topic}</CardTitle>
              <IntentBadge intent={cluster.intent} />
              <span className="text-xs text-muted-foreground">
                {cluster.keywords.length} keyword{cluster.keywords.length !== 1 ? 's' : ''}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {cluster.keywords.map((kw) => (
                <Badge key={kw} variant="outline" className="text-xs">
                  {kw}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

export function KeywordResearchTab({ siteId, tier }: { siteId: string; tier: string }) {
  const [seed, setSeed] = useState('')
  const [activeSearch, setActiveSearch] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [intents, setIntents] = useState<Record<string, string>>({})
  const [viewMode, setViewMode] = useState<'flat' | 'clusters'>('flat')
  const [clusters, setClusters] = useState<KeywordCluster[] | null>(null)

  const { data, isLoading } = useKeywordSuggestions(siteId, activeSearch)
  const searchIntent = useSearchIntent(siteId)
  const importKeywords = useImportKeywords(siteId)
  const clusterMutation = useClusterKeywords(siteId)

  if (tier !== 'managed') return <ManagedTierGate />

  const suggestions = data?.suggestions ?? []
  const trackedSet = new Set(data?.trackedKeywords ?? [])

  const toggleSelect = (keyword: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(keyword)) next.delete(keyword)
      else next.add(keyword)
      return next
    })
  }

  const handleSearch = () => {
    const trimmed = seed.trim()
    if (!trimmed) return
    setActiveSearch(trimmed)
    setSelected(new Set())
    setIntents({})
    setClusters(null)
    setViewMode('flat')
  }

  const handleGetIntent = () => {
    const keywords = selected.size > 0
      ? Array.from(selected)
      : suggestions.map((s) => s.keyword).slice(0, 100)

    searchIntent.mutate(keywords, {
      onSuccess: (result) => {
        const map: Record<string, string> = {}
        for (const item of result.intents) {
          map[item.keyword.toLowerCase()] = item.intent
        }
        setIntents((prev) => ({ ...prev, ...map }))
        toast.success(`Intent data loaded for ${result.intents.length} keywords`)
      },
      onError: () => toast.error('Failed to fetch intent data'),
    })
  }

  const handleImport = () => {
    const keywords = Array.from(selected)
    if (!keywords.length) return
    importKeywords.mutate(keywords, {
      onSuccess: (result) => {
        toast.success(`${result.added} keyword${result.added !== 1 ? 's' : ''} added to tracking`)
        setSelected(new Set())
      },
      onError: () => toast.error('Failed to import keywords'),
    })
  }

  const handleCluster = () => {
    const keywords = suggestions.map((s) => s.keyword)
    if (!keywords.length) return
    clusterMutation.mutate(keywords, {
      onSuccess: (result) => {
        if (result.clusters?.length) {
          setClusters(result.clusters)
          setViewMode('clusters')
          toast.success(`Grouped into ${result.clusters.length} topic clusters`)
        } else {
          toast.error('No clusters were generated')
        }
      },
      onError: () => toast.error('Failed to cluster keywords'),
    })
  }

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <Input
              placeholder="Enter a seed keyword (e.g. 'web design newcastle')"
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            />
            <Button onClick={handleSearch} disabled={isLoading || !seed.trim()}>
              {isLoading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Search className="mr-1 h-4 w-4" />}
              Research
            </Button>
          </div>
          {!activeSearch && (
            <p className="text-xs text-muted-foreground mt-2">
              Enter a seed keyword to discover related keyword opportunities with volume, CPC, and competition data.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {activeSearch && (
        <>
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">
                  Suggestions for &ldquo;{activeSearch}&rdquo;
                  {suggestions.length > 0 && <span className="text-muted-foreground font-normal ml-2">({suggestions.length})</span>}
                </CardTitle>
                <div className="flex gap-2">
                  {/* View mode toggle */}
                  {suggestions.length > 0 && (
                    <div className="flex border rounded-md">
                      <Button
                        size="sm"
                        variant={viewMode === 'flat' ? 'default' : 'ghost'}
                        className="rounded-r-none px-2.5"
                        onClick={() => setViewMode('flat')}
                      >
                        <List className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant={viewMode === 'clusters' ? 'default' : 'ghost'}
                        className="rounded-l-none px-2.5"
                        onClick={() => {
                          if (!clusters) {
                            handleCluster()
                          } else {
                            setViewMode('clusters')
                          }
                        }}
                        disabled={clusterMutation.isPending}
                      >
                        {clusterMutation.isPending
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          : <Layers className="h-3.5 w-3.5" />
                        }
                      </Button>
                    </div>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleGetIntent}
                    disabled={searchIntent.isPending || suggestions.length === 0}
                  >
                    {searchIntent.isPending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
                    Get Intent
                  </Button>
                  {selected.size > 0 && (
                    <Button size="sm" onClick={handleImport} disabled={importKeywords.isPending}>
                      Track Selected ({selected.size})
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 10 }).map((_, i) => (
                    <Skeleton key={i} className="h-10 w-full" />
                  ))}
                </div>
              ) : suggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No suggestions found. Try a different seed keyword.
                </p>
              ) : viewMode === 'clusters' && clusters ? (
                <ClusteredView clusters={clusters} />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="w-8 py-2" />
                        <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Keyword</th>
                        <th className="text-right py-2 px-2 font-medium text-muted-foreground w-20">Volume</th>
                        <th className="text-right py-2 px-2 font-medium text-muted-foreground w-16">CPC</th>
                        <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">Comp</th>
                        <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">Intent</th>
                        <th className="text-center py-2 px-2 font-medium text-muted-foreground w-20 hidden sm:table-cell">Trend</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suggestions.map((item: KeywordSuggestion) => {
                        const isTracked = trackedSet.has(item.keyword.toLowerCase())
                        const intent = intents[item.keyword.toLowerCase()]
                        return (
                          <tr key={item.keyword} className={`border-b last:border-0 ${isTracked ? 'opacity-50' : ''}`}>
                            <td className="py-1.5 text-center">
                              {isTracked ? (
                                <Badge variant="outline" className="text-[10px] px-1">Tracked</Badge>
                              ) : (
                                <Checkbox
                                  checked={selected.has(item.keyword)}
                                  onCheckedChange={() => toggleSelect(item.keyword)}
                                />
                              )}
                            </td>
                            <td className="py-1.5 pr-4">
                              <span className="truncate block max-w-[300px]" title={item.keyword}>{item.keyword}</span>
                            </td>
                            <td className="text-right py-1.5 px-2 text-muted-foreground">
                              {item.search_volume != null ? formatNumber(item.search_volume) : '-'}
                            </td>
                            <td className="text-right py-1.5 px-2 text-muted-foreground">
                              {item.cpc != null ? `$${item.cpc.toFixed(2)}` : '-'}
                            </td>
                            <td className="text-center py-1.5 px-2">
                              {item.competition_level ? (
                                <Badge
                                  variant="outline"
                                  className={`text-[10px] px-1 ${
                                    item.competition_level === 'LOW' ? 'text-green-600 dark:text-green-400 border-green-600/30' :
                                    item.competition_level === 'MEDIUM' ? 'text-yellow-600 dark:text-yellow-400 border-yellow-600/30' :
                                    'text-red-600 dark:text-red-400 border-red-600/30'
                                  }`}
                                >
                                  {item.competition_level}
                                </Badge>
                              ) : '-'}
                            </td>
                            <td className="text-center py-1.5 px-2">
                              {intent ? <IntentBadge intent={intent} /> : '-'}
                            </td>
                            <td className="text-center py-1.5 px-2 hidden sm:table-cell">
                              <TrendSparkline data={item.monthly_searches} />
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
